// src/pages/plans/ArtAdvisorPremiumDemo.jsx
// Landing du plan Art Advisor Premium (/plans/art-advisor-premium/demo). Refaite le 2026-10-05
// sur le modèle des pages /notaires et /cercle-experts.
//
// Ce qui distingue Premium d'Art Advisor — chaque ligne vérifiée dans le code :
//  • clients illimités (CollaboratorsManager : art_advisor_premium → client 999) ;
//  • rapports co-marqués (CollectionsReports : whiteLabelPlans) ;
//  • analyses Vision lancées sur la collection de chacun de vos clients, même Standard
//    (confirmé par Vanessa, utilisatrice Premium) ;
//  • jusqu'à 3 assistants (CollaboratorsManager : assistant 3) ;
//  • rapport de valorisation (outil badgé Premium dans ExpertDashboard).
// API et intégrations : PAS livrées → présentées comme « en cours de développement ».
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDemoPage } from "@/lib/useDemoPage";
import { BRAND_NAME } from "@/config/brand";
import BrandMark from "@/components/BrandMark";

// Ligne de comparaison : [critère, Art Advisor, Premium]
const COMPARE = [
  ["Clients", "Jusqu'à 5 collections clients", "Illimités"],
  ["Rapports", "Rapports PDF à votre disposition", "Co-marqués : votre logo, le nom de votre cabinet"],
  ["Analyses Vision", "—", "Lancées sur la collection de chacun de vos clients, même en formule Standard"],
  ["Rapport de valorisation", "—", "Inclus"],
  ["Assistants", "1 assistant", "Jusqu'à 3 assistants"],
  ["Mises en relation avec les collectionneurs", "Incluses, illimitées", "Incluses, illimitées"],
  ["Commission sur vos missions", "0 %", "0 %"],
];

const REASONS = [
  { t: "Votre portefeuille dépasse 5 clients", d: "Plus de plafond : vous accompagnez autant de collectionneurs que votre activité le demande, sans changer d'outil." },
  { t: "Vous remettez des rapports à votre nom", d: "Vos clients reçoivent des documents à votre identité (logo, nom du cabinet), pas à celle de la plateforme." },
  { t: "Vous voulez des analyses pour chacun", d: "Vous lancez les analyses Vision sur la collection de chaque client, même s'il est en formule Standard. Vous relisez et validez chaque résultat avant de le partager." },
  { t: "Vous déléguez à un assistant", d: "Jusqu'à 3 assistants travaillent dans votre espace, avec des droits adaptés." },
];

const SHOT_CANDIDATES = [
  { src: "/images/advisor/propositions-detail.png", title: "Vos propositions d'acquisition, prêtes à partager", description: "Composez une sélection d'œuvres pour un client, avec prix demandé et argumentaire par œuvre. Un lien de 14 jours suffit à recueillir ses réactions." },
  { src: "/images/advisor/dashboard-multi-clients.png", title: "Tous vos dossiers clients, sans plafond", description: "Chaque client a son dossier, avec ou sans compte. Passé 5 clients, rien ne change dans votre espace." },
];

export default function ArtAdvisorPremiumDemo() {
  const navigate = useNavigate();
  const trackCta = useDemoPage("art-advisor-premium");
  const [shots, setShots] = useState([]);

  useEffect(() => {
    let alive = true;
    SHOT_CANDIDATES.forEach((s) => {
      const img = new Image();
      img.onload = () => {
        if (alive) setShots((prev) => (prev.some((p) => p.src === s.src) ? prev : [...prev, s].sort((a, b) => SHOT_CANDIDATES.findIndex((c) => c.src === a.src) - SHOT_CANDIDATES.findIndex((c) => c.src === b.src))));
      };
      img.src = s.src;
    });
    return () => { alive = false; };
  }, []);

  const go = (path) => { trackCta(path); navigate(path); };
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="min-h-screen bg-[#f9f7f4] text-[#1a1a1a]" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
      {/* NAV */}
      <header className="sticky top-0 z-30 bg-[#f9f7f4]/95 backdrop-blur border-b border-[#e5e0d8]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <BrandMark className="text-2xl tracking-[0.15em]" />
          <div className="flex items-center gap-4">
            <button onClick={() => scrollTo("difference")} className="hidden md:inline text-sm text-[#6b6459] hover:text-black">Ce qui change</button>
            <button onClick={() => scrollTo("quand")} className="hidden md:inline text-sm text-[#6b6459] hover:text-black">Quand passer Premium</button>
            <Link to="/plans/art-advisor/demo" className="hidden md:inline text-sm text-[#6b6459] hover:text-black">Art Advisor</Link>
            <button onClick={() => go("/contact?plan=art_advisor_premium&demo=true")} className="text-sm px-4 py-2 bg-[#1a1a1a] text-[#f9f7f4] rounded-md hover:bg-black">
              Demander une démonstration
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-6 pt-14 md:pt-20 pb-14">
        <div className="text-xs tracking-[0.18em] uppercase text-[#8a8378] mb-5">Plan Art Advisor Premium · pour les cabinets qui grandissent</div>
        <h1 className="text-4xl md:text-5xl font-bold leading-[1.1] max-w-4xl">
          Aucune limite de clients. Des rapports à votre nom.
        </h1>
        <p className="mt-6 text-lg text-[#4a453d] leading-relaxed max-w-2xl">
          Tout le plan Art Advisor, sans plafond, avec vos rapports co-marqués et les analyses Vision pour chacun de vos clients.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <button onClick={() => go("/contact?plan=art_advisor_premium&demo=true")} className="px-6 py-3 bg-[#1a1a1a] text-[#f9f7f4] rounded-md text-base hover:bg-black">
            Demander une démonstration
          </button>
          <button onClick={() => go("/pricing")} className="px-6 py-3 border border-[#1a1a1a] rounded-md text-base hover:bg-[#1a1a1a] hover:text-[#f9f7f4] transition-colors">
            Comparer les formules
          </button>
        </div>
        <p className="mt-4 text-sm text-[#8a8378]">199 € par mois · aucune commission sur vos missions · mises en relation incluses</p>
      </section>

      {/* CE QUI CHANGE */}
      <section id="difference" className="border-y border-[#e5e0d8] bg-white">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-xs tracking-[0.18em] uppercase text-[#8a8378] mb-3">Ce qui change</div>
          <h2 className="text-3xl font-bold mb-2">Premium, ligne par ligne.</h2>
          <p className="text-[#4a453d] mb-10 max-w-2xl">Tout ce que contient Art Advisor reste vrai. Voici ce que Premium ajoute.</p>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[640px]">
              <thead>
                <tr className="border-b-2 border-[#1a1a1a] text-sm">
                  <th className="py-3 pr-4 font-normal text-[#8a8378]"></th>
                  <th className="py-3 pr-4 font-bold">Art Advisor <span className="font-normal text-[#8a8378]">· 99 €</span></th>
                  <th className="py-3 font-bold">Premium <span className="font-normal text-[#8a8378]">· 199 €</span></th>
                </tr>
              </thead>
              <tbody>
                {COMPARE.map(([k, a, p]) => (
                  <tr key={k} className="border-b border-[#e5e0d8] align-top">
                    <td className="py-4 pr-4 text-sm text-[#6b6459] w-1/4">{k}</td>
                    <td className="py-4 pr-4 text-sm text-[#4a453d] w-[30%]">{a}</td>
                    <td className="py-4 text-sm font-semibold">{p}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CAPTURES RÉELLES */}
      {shots.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-xs tracking-[0.18em] uppercase text-[#8a8378] mb-3">Dans l'espace</div>
          <h2 className="text-3xl font-bold mb-10">Ce que vous verrez, tel quel.</h2>
          <div className={`grid gap-8 ${shots.length > 1 ? "md:grid-cols-2" : ""}`}>
            {shots.map((shot) => (
              <figure key={shot.src} className="bg-white rounded-2xl p-4 border border-[#e5e0d8]">
                <img src={shot.src} alt={shot.title} className="w-full rounded-lg border border-[#ece6da]" loading="lazy" />
                <figcaption className="mt-4 px-1">
                  <span className="block text-lg font-bold mb-1">{shot.title}</span>
                  <span className="block text-sm text-[#4a453d] leading-relaxed">{shot.description}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* QUAND PASSER PREMIUM */}
      <section id="quand" className="border-y border-[#e5e0d8] bg-white">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-xs tracking-[0.18em] uppercase text-[#8a8378] mb-3">Quand passer Premium</div>
          <h2 className="text-3xl font-bold mb-10">Vous le saurez à l'un de ces quatre signes.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {REASONS.map((r, i) => (
              <div key={r.t} className="border border-[#e5e0d8] rounded-xl p-5 bg-[#f9f7f4]">
                <div className="w-8 h-8 rounded-full bg-[#1a1a1a] text-[#f9f7f4] flex items-center justify-center text-sm font-bold mb-3">{i + 1}</div>
                <h3 className="font-bold mb-2">{r.t}</h3>
                <p className="text-sm text-[#4a453d] leading-relaxed">{r.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ENGAGEMENTS COMMUNS + À VENIR */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="rounded-2xl border border-[#e5e0d8] bg-white p-7">
          <div className="text-xs tracking-[0.18em] uppercase text-[#8a8378] mb-3">Ce qui ne change pas</div>
          <h3 className="text-xl font-bold mb-3">Vos clients restent les vôtres.</h3>
          <ul className="text-sm text-[#4a453d] leading-relaxed space-y-2">
            <li>Aucune commission sur vos missions : la base de données l'interdit.</li>
            <li>Vos notes internes ne sont visibles ni de votre client ni de l'équipe {BRAND_NAME}.</li>
            <li>Les mises en relation avec les collectionneurs de la plateforme sont incluses, sans limite.</li>
            <li>Vous relisez et validez chaque analyse avant de la partager.</li>
          </ul>
        </div>
        <p className="mt-6 text-sm text-[#6b6459]">
          <strong>En cours de développement :</strong> API et intégrations (CRM, comptabilité), pas encore disponibles.{" "}
          <button onClick={() => go("/contact?plan=art_advisor_premium")} className="underline hover:text-black">Dites-nous ce qu'il vous faut.</button>
        </p>
      </section>

      {/* CTA FINAL */}
      <section className="bg-[#1a1a1a] text-[#f9f7f4]">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h2 className="text-3xl font-bold mb-4">Voyez Premium sur un de vos dossiers.</h2>
          <p className="text-[#d8d2c6] leading-relaxed max-w-2xl mx-auto mb-8">
            Nous cadrons ensemble votre usage avant tout engagement.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button onClick={() => go("/contact?plan=art_advisor_premium&demo=true")} className="px-7 py-3 bg-[#c9a961] text-[#1a1a1a] font-semibold rounded-md hover:bg-[#b9975a]">
              Demander une démonstration
            </button>
            <button onClick={() => go("/pricing")} className="px-7 py-3 border border-[#f9f7f4]/60 rounded-md hover:bg-[#f9f7f4]/10">
              Voir tous les plans
            </button>
          </div>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-6 py-10 text-xs text-[#8a8378] flex flex-wrap gap-4 justify-between">
        <span>© {new Date().getFullYear()} {BRAND_NAME}</span>
        <span className="flex gap-4">
          <Link to="/mentions-legales" className="hover:text-black">Mentions légales</Link>
          <Link to="/privacy-policy" className="hover:text-black">Confidentialité</Link>
        </span>
      </footer>
    </div>
  );
}
